import type { VideoItem } from '@/mocks/videos';
import { videos } from '@/mocks/videos';

export type SortKey = 'latest' | 'popular' | 'duration';

export interface VideoFilter {
  category?: string;
  region?: string;
  perspective?: string;
  year?: number | string;
  keyword?: string;
}

export function getVideoById(id: string | undefined): VideoItem | undefined {
  if (!id) return undefined;
  return videos.find((item) => item.id === id);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return `${views}`;
}

export function durationToSeconds(duration: string): number {
  const parts = duration.split(':').map((item) => Number(item) || 0);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return parts[0] * 60 + parts[1];
}

export function getLatest(limit = 9): VideoItem[] {
  return [...videos]
    .sort((a, b) => b.uploadDate.localeCompare(a.uploadDate))
    .slice(0, limit);
}

export function getPopular(limit = 8): VideoItem[] {
  return [...videos].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getFeatured(): VideoItem[] {
  return videos.filter((item) => item.featured);
}

export function countByCategory(name: string): number {
  return videos.filter((item) => item.category === name).length;
}

export function filterVideos(filter: VideoFilter): VideoItem[] {
  const keyword = filter.keyword?.trim().toLowerCase() || '';

  return videos.filter((item) => {
    if (filter.category && item.category !== filter.category) return false;
    if (filter.region && item.region !== filter.region) return false;
    if (filter.perspective && item.perspective !== filter.perspective) return false;
    if (filter.year && String(item.year) !== String(filter.year)) return false;

    if (keyword) {
      const haystack = [
        item.title,
        item.category,
        item.region,
        item.perspective,
        item.resolution,
        item.description,
        item.tags.join(' '),
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }

    return true;
  });
}

export function sortVideos(list: VideoItem[], sort: SortKey): VideoItem[] {
  const next = [...list];
  if (sort === 'popular') {
    return next.sort((a, b) => b.views - a.views);
  }
  if (sort === 'duration') {
    return next.sort((a, b) => durationToSeconds(b.duration) - durationToSeconds(a.duration));
  }
  return next.sort((a, b) => b.uploadDate.localeCompare(a.uploadDate));
}

export function getRelated(video: VideoItem, limit = 6): VideoItem[] {
  const scored = videos
    .filter((item) => item.id !== video.id)
    .map((item) => {
      let score = 0;
      if (item.category === video.category) score += 3;
      if (item.region === video.region) score += 1;
      if (item.perspective === video.perspective) score += 1;
      score += item.tags.filter((tag) => video.tags.includes(tag)).length * 2;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);

  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getSearchSuggestions(limit = 4): VideoItem[] {
  return getPopular(limit);
}